import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import reviewsData from './reviews.json';
import './CustomerReviews.scss';

const StarRating = ({ count }) => {
  return (
    <span className="star-rating" aria-label={`${count} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <span key={i} className={`star ${i < count ? 'filled' : ''}`}>★</span>
      ))}
    </span>
  );
};

const TEXT_LIMIT = 200; // characters before truncating

const ImageLightbox = ({ imageSrc, altText, onClose }) => {
  return ReactDOM.createPortal(
    <div 
      className="image-lightbox" 
      onClick={onClose}
      role="dialog"
      aria-label="Expanded image view"
    >
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <img
          src={imageSrc}
          alt={altText}
        />
      </div>
      <button 
        className="lightbox-close" 
        onClick={onClose}
        aria-label="Close expanded image"
      >
        ×
      </button>
    </div>,
    document.body
  );
};

const ReviewCard = ({ review }) => {
  const [expandedImage, setExpandedImage] = useState(null);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  const isLongText = review.reviewText.length > TEXT_LIMIT;
  const displayText = isTextExpanded || !isLongText 
    ? review.reviewText 
    : review.reviewText.slice(0, TEXT_LIMIT) + '...';

  return (
    <article className="review-card">
      <header className="review-header">
        <div className="reviewer-info">
          <h3 className="reviewer-name">{review.name}</h3>
          <div className="reviewer-meta">
            {review.isLocalGuide && (
              <span className="local-guide-badge">Local Guide</span>
            )}
            <span className="review-stats">
              {review.numberOfReviews} reviews · {review.numberOfPhotos} photos
            </span>
          </div>
        </div>
        <div className="review-rating">
          <StarRating count={review.numberOfStars} />
          <span className="time-posted">{review.timePosted}</span>
        </div>
      </header>

      <div className="review-body">
        <p className="review-text">{displayText}</p>
        {isLongText && (
          <button 
            className="expand-text-btn"
            onClick={() => setIsTextExpanded(!isTextExpanded)}
            aria-expanded={isTextExpanded}
          >
            {isTextExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
        
        {review.dishes && review.dishes.length > 0 && (
          <div className="dishes-mentioned">
            <span className="dishes-label">Dishes mentioned:</span>
            <div className="dishes-list">
              {review.dishes.map((dish, idx) => (
                <span key={idx} className="dish-tag">{dish}</span>
              ))}
            </div>
          </div>
        )}

        {review.ratings && (
          <div className="sub-ratings">
            {review.ratings.food && (
              <span className="sub-rating">
                <span className="sub-rating-label">Food</span>
                <StarRating count={review.ratings.food} />
              </span>
            )}
            {review.ratings.service && (
              <span className="sub-rating">
                <span className="sub-rating-label">Service</span>
                <StarRating count={review.ratings.service} />
              </span>
            )}
            {review.ratings.atmosphere && (
              <span className="sub-rating">
                <span className="sub-rating-label">Atmosphere</span>
                <StarRating count={review.ratings.atmosphere} />
              </span>
            )}
          </div>
        )}
      </div>

      {review.images && review.images.length > 0 && (
        <div className="review-images">
          {review.images.map((img, idx) => (
            <button
              key={idx}
              className="review-image-button"
              onClick={() => setExpandedImage(idx)}
              aria-label={`View photo ${idx + 1} from ${review.name}`}
            >
              <img
                src={require(`./${img}`)}
                alt={`Photo from ${review.name}'s review`}
                className="review-image"
              />
            </button>
          ))}
        </div>
      )}

      {expandedImage !== null && review.images && (
        <ImageLightbox
          imageSrc={require(`./${review.images[expandedImage]}`)}
          altText={`Expanded photo from ${review.name}'s review`}
          onClose={() => setExpandedImage(null)}
        />
      )}
    </article>
  );
};

const CustomerReviews = () => {
  return (
    <section className="customer-reviews-section">
      <h2 className="reviews-header">What Our Guests Say</h2>
      {/* <p className="reviews-subtitle">Real reviews from real customers</p> */}
      
      <div className="reviews-container">
        {reviewsData.map((review, idx) => (
          <ReviewCard key={idx} review={review} />
        ))}
      </div>

      <div className="reviews-cta">
        <a 
          href="https://www.google.com/search?sca_esv=95f66cc136df0cea&sxsrf=AE3TifNbni5DFrtj9sHGsj9Z-D_OnNE6dg:1767725054165&q=domenicos+pasadena+best&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8s77cBLjWG7cTBP0Uj61KNVNL6nrixU8eQZ8zkpaX5RM9nGkaGwj292pU6JAC30AwRhLSW89kDadsId4QhEvkdZYaD7oJ&sa=X&ved=2ahUKEwj35qf_yPeRAxXql-4BHTH7Hw0QrrQLegQIGhAB&biw=2287&bih=1381&dpr=2" 
          target="_blank" 
          rel="noopener noreferrer"
          className="google-reviews-link"
        >
          See More Reviews on Google →
        </a>
      </div>
    </section>
  );
};

export default CustomerReviews;
